import { weightedRoll, type RandomSource, type WeightedEntry } from "@yjsgames/weighted-roll";
import { RELIC_DEFS_ORDERED } from "../data/balance.js";

/**
 * 미스터리(?) 블록 보상 결정 — 순수(Phaser 비의존, rng·데이터 주입으로 결정론).
 * 모든 수치(배수 값·가중치·확률)는 호출자가 global.tsv(MYSTERY_BRICK)에서 주입 — 코드 리터럴 0.
 */

/**
 * 골드 배수 추첨 — (값,가중치) 엔트리(MYSTERY_BRICK.goldMultipliers)에서 weight 비례로 1개.
 * 엔트리가 비면 첫 값(없으면 1배) 폴백. 단일 rng 호출.
 */
export function rollGoldMultiplier(
  rng: RandomSource,
  entries: readonly WeightedEntry<number>[],
): number {
  return weightedRoll(rng, entries, entries[0]?.value ?? 1);
}

/**
 * 다이아 지급량 추첨 — (값,가중치) 엔트리(MYSTERY_BRICK.diamondTiers)에서 weight 비례로 1개.
 * 엔트리가 비면 첫 값(없으면 1개) 폴백. rollGoldMultiplier 동형.
 */
export function rollDiamondAmount(
  rng: RandomSource,
  entries: readonly WeightedEntry<number>[],
): number {
  return weightedRoll(rng, entries, entries[0]?.value ?? 1);
}

/**
 * 확률 게이트 — chance(0~1) 미만이면 true. chance≤0이면 항상 false, ≥1이면 항상 true(rng 미호출).
 * 다이아/유물 "가끔" 지급 판정 공용.
 */
export function rollChance(rng: RandomSource, chance: number): boolean {
  if (chance <= 0) return false;
  if (chance >= 1) return true;
  return rng() < chance;
}

/**
 * 랜덤 유물 id 픽 — RELIC_DEFS_ORDERED 풀에서 등급 가중(gradeWeights, 미지정 시 균등) 1개.
 * 풀이 비면 null. 실제 지급(RelicController.acquire)은 호출자 책임. 단일 rng 호출.
 */
export function rollRelicId(
  rng: RandomSource,
  gradeWeights?: Readonly<Record<string, number>>,
): string | null {
  const pool = RELIC_DEFS_ORDERED;
  if (pool.length === 0) return null;
  const entries: WeightedEntry<string>[] = pool.map((d) => ({
    value: d.relicId,
    weight: gradeWeights ? (gradeWeights[d.grade] ?? 0) : 1,
  }));
  return weightedRoll(rng, entries, pool[0]!.relicId);
}

/** 미스터리 보상 추첨 설정 (global.tsv MYSTERY_BRICK 주입). */
export interface MysteryResolveConfig {
  goldMultipliers: readonly WeightedEntry<number>[];
  diamondTiers: readonly WeightedEntry<number>[];
  diamondChance: number;
  relicChance: number;
}

/** 지급 가능 상태 + 치트 강제 여부 (호출자가 컨트롤러에서 꺼내 주입). */
export interface MysteryResolveState {
  canGrantDiamond: boolean;
  canGrantRelic: boolean;
  /** 치트 ON이면 확률·일일 한도 무시 항상 지급. */
  force: boolean;
}

/** 미스터리 상자 1회에서 실제 지급할 보상 종류. 한 상자에서 하나만 선택한다. */
export type MysteryRewardKind = "gold" | "diamond" | "relic";

/** 보상 결정 결과 — 순수. 부작용(addGold·acquire·FX)은 호출자(BattlefieldRunner) 책임. */
export interface MysteryResolveResult {
  rewardKind: MysteryRewardKind;
  goldMultiplier: number;
  tryDiamond: boolean;
  diamondAmount: number;
  /** tryRelic=true여도 acquire(relicId) 실패 가능 — 호출자가 실제 acquire 결과로 확인. */
  tryRelic: boolean;
  relicId: string | null;
}

/**
 * 미스터리(?) 블록 보상 결정 묶음 — 골드/다이아/유물 중 하나만 rng 주입으로 결정.
 * 모든 랜덤·상태 의존을 파라미터화하므로 결정론 테스트 가능.
 *
 * BattlefieldRunner.grantMysteryReward가 이 함수를 호출해 결과를 소비하는 셸(Humble View) 패턴.
 */
export function resolveMysteryRewards(
  rng: RandomSource,
  config: MysteryResolveConfig,
  state: MysteryResolveState,
): MysteryResolveResult {
  const tryDiamond = state.force || (rollChance(rng, config.diamondChance) && state.canGrantDiamond);
  if (tryDiamond) {
    return {
      rewardKind: "diamond",
      goldMultiplier: 1,
      tryDiamond: true,
      diamondAmount: rollDiamondAmount(rng, config.diamondTiers),
      tryRelic: false,
      relicId: null,
    };
  }

  const tryRelic = state.force || (rollChance(rng, config.relicChance) && state.canGrantRelic);
  const relicId = tryRelic ? rollRelicId(rng) : null;
  if (tryRelic && relicId) {
    return {
      rewardKind: "relic",
      goldMultiplier: 1,
      tryDiamond: false,
      diamondAmount: 0,
      tryRelic: true,
      relicId,
    };
  }

  return {
    rewardKind: "gold",
    goldMultiplier: rollGoldMultiplier(rng, config.goldMultipliers),
    tryDiamond: false,
    diamondAmount: 0,
    tryRelic: false,
    relicId: null,
  };
}

/**
 * 보상 규모 → fly 아이콘 개수 (1~4) — 양이 클수록 여러 개 퍼뜨려 보여준다. 순수.
 * 골드는 추첨 배수(x3→1, x10→2, x30→3, x50→4), 다이아는 획득 개수(1→1, 10→2)를 tier로 넘긴다.
 */
export function mysteryBurstCount(tier: number): number {
  if (tier >= 50) return 4;
  if (tier >= 30) return 3;
  if (tier >= 10) return 2;
  return 1;
}
